import {
  AltType,
  NotificationChannel,
  NotificationConfig,
  ReceiverType,
} from '@/types/notification'
import { Ref, reactive, ref } from 'vue'

import Calendar, { GuestCollectionMode } from '@/models/calendar'
import Email from '@/models/email'
import { EventNotificationService } from '@/class/services/EventNotificationService'
import { formatHTMLForTipTap } from '@gohighlevel/ghl-text-editor'
import { Editor } from '@tiptap/vue-3'
import { getDefaultEmailSubjectAndBody, getDefaultWhatsAppTemplateLabel } from '../utils/default-notification-template'
import AppState, { getWhatsAppTemplates } from './app'

const I18N_DEFAULT_RECEIVERS = new Set<string>([
  ReceiverType.CONTACT,
  ReceiverType.ASSIGNED_USER,
  ReceiverType.GUEST,
  ReceiverType.EMAILS,
  ReceiverType.PHONE_NUMBERS,
])

/** Pack defaults from `default/notification-configs`, keyed for Settings Reset reuse. */
const localizedDefaultsCache = new Map<string, NotificationConfig[]>()

function buildDefaultsCacheKey(
  altId: string,
  channel: string,
  notificationType: string
): string {
  return `${altId}|${channel}|${notificationType}`
}

function supportsBackendI18nDefaults(
  channel?: NotificationChannel | string,
  receiverType?: ReceiverType | string
): boolean {
  const channelStr = channel?.toString()
  return (
    (channelStr === NotificationChannel.EMAIL ||
      channelStr === NotificationChannel.SMS) &&
    !!receiverType &&
    I18N_DEFAULT_RECEIVERS.has(receiverType)
  )
}

function findPackDefaultForReceiver(
  defaults: Array<{ receiverType: string; subject?: string; body?: string }>,
  receiverType: string
) {
  return (
    defaults.find(item => item.receiverType === receiverType) ||
    defaults[0]
  )
}

function applyPackDefaultToLocal(
  local: NotificationConfig,
  pack: { subject?: string; body?: string }
): NotificationConfig {
  return {
    ...local,
    subject: pack.subject ?? '',
    body: pack.body || '',
    templateId: '',
    isDefault: true,
  }
}

const receiverTypes: string[] = Object.values(ReceiverType)

export const getReceiverTypesForChannel = (channel: string): string[] => {
  const channelStr = channel?.toString()

  if (channel === NotificationChannel.SMS) {
    // Ensure correct order: contact, assigned user, guest, additional phone numbers
    return [
      ReceiverType.CONTACT,
      ReceiverType.ASSIGNED_USER,
      ReceiverType.GUEST,
      ReceiverType.PHONE_NUMBERS,
    ]
  } else if (channel === NotificationChannel.EMAIL) {
    // Ensure correct order: contact, assigned user, guest, additional emails
    return [ReceiverType.CONTACT, ReceiverType.ASSIGNED_USER, ReceiverType.GUEST, ReceiverType.EMAILS]
  } else if (channelStr === 'whatsapp') {
    // For WhatsApp, only support contact, assigned user, and additional phone numbers
    return [ReceiverType.CONTACT, ReceiverType.ASSIGNED_USER, ReceiverType.PHONE_NUMBERS]
  } else {
  // For in-app notifications: contact, assigned user, guest
  return [ReceiverType.CONTACT, ReceiverType.ASSIGNED_USER, ReceiverType.GUEST]
}
}

export const guestReceiverVisibleForChannel = (
  channel: NotificationChannel | string,
  enableGuests: boolean,
  guestCollectionMode?: GuestCollectionMode | string
): boolean => {
  if (!enableGuests) {
    return false
  }
  const mode = guestCollectionMode || GuestCollectionMode.EMAIL
  if (channel === NotificationChannel.EMAIL) {
    return mode !== GuestCollectionMode.PHONE
  }
  if (channel === NotificationChannel.SMS) {
    return mode !== GuestCollectionMode.EMAIL
  }
  return true
}

/**
 * Sort notifications array by receiver type according to the desired order for the channel
 * This ensures consistent ordering regardless of backend response order
 */
export const sortNotificationsByReceiverType = (
  notifications: NotificationConfig[],
  channel: string
): NotificationConfig[] => {
  const desiredOrder = getReceiverTypesForChannel(channel)

  return notifications.sort((a, b) => {
    const indexA = desiredOrder.indexOf(a.receiverType)
    const indexB = desiredOrder.indexOf(b.receiverType)

    // If receiver type not found in desired order, put it at the end
    const orderA = indexA === -1 ? desiredOrder.length : indexA
    const orderB = indexB === -1 ? desiredOrder.length : indexB

    return orderA - orderB
  })
}

export const eventNotificationState = reactive({
  calendarId: '',
  isEventCalendar: false,
  isCustomFormSelected: false,
  calendar: {} as Calendar | null,
  isLoading: false,
  saving: false,
  disableSave: false,
  /** Incremented per configurator while merge-field syntax is invalid; disables Save in notification modal */
  templateVariableErrorCount: 0,
  showEmailTemplateRefresh: false,
  eventNotificationData: [],
  eventNotifications: [] as NotificationConfig[],
  smsNotifications: [] as NotificationConfig[],
  whatsappNotifications: [] as NotificationConfig[],
  accordions: [] as {
    id: string
    isOpen: boolean
    isChecked: boolean
  }[],
  lastOpenedAccordion: null as number | null,
  inAppNotification: {} as NotificationConfig,
  enableNotification: false,
  contact: {},
  guest: {},
  assignedUser: {},
  additionalEmails: {},
})
export const resetState = () => {
  eventNotificationState.eventNotifications = []
  eventNotificationState.smsNotifications = []
  eventNotificationState.whatsappNotifications = []
  eventNotificationState.contact = {}
  eventNotificationState.guest = {}
  eventNotificationState.assignedUser = {}
  eventNotificationState.additionalEmails = {}
  eventNotificationState.templateVariableErrorCount = 0
  localizedDefaultsCache.clear()
}
export class EventNotification {
  actionTemplate: Ref<Email | undefined>
  state = reactive({} as NotificationConfig)
  editor: Editor
  toAddress: Ref<string | undefined>
  testPhoneNumber: Ref<string | undefined>
  open = ref(false)
  checked = ref(false)
  constructor(props: NotificationConfig) {
    this.actionTemplate = ref<Email>(new Email())
    this.editor = [] as any
    this.toAddress = ref<string | undefined>(undefined)
    this.testPhoneNumber = ref<string | undefined>(undefined)
    this.open = ref(false)
    this.checked = ref(false)
    this.state = reactive({
      _id: props._id,
      altType: props.altType,
      altId: props.altId,
      receiverType: props.receiverType,
      additionalEmailIds: props.additionalEmailIds,
      additionalPhoneNumbers: props.additionalPhoneNumbers,
      channel: props.channel,
      notificationType: props.notificationType,
      isActive: props.isActive,
      isDefault: props.isDefault,
      templateId: props.templateId,
      body: formatHTMLForTipTap(props.body || ''),
      subject: props.subject,
      beforeTime: props.beforeTime || [],
      afterTime: props.afterTime || [],
      selectedUsers: props.selectedUsers,
      fromAddress: props.fromAddress || '',
      fromName: props.fromName || '',
      deleted: props.deleted ?? false,
    })
  }
}

export const updateStateOfNotification = (
  id: string,
  state: Partial<NotificationConfig>
) => {
  const index = eventNotificationState.eventNotifications.findIndex(
    notification => notification._id === id
  )
  if (index !== -1) {
    eventNotificationState.eventNotifications[index] = {
      ...eventNotificationState.eventNotifications[index],
      ...state,
    }
  } else {
    console.warn(`Notification with id ${id} not found.`)
  }
}

export const getDefaultNotifications = (
  notification: Partial<NotificationConfig>
) => {
  const defaultNotifications = [] as NotificationConfig[]
  const channel = notification.channel || NotificationChannel.EMAIL
  const channelReceiverTypes = getReceiverTypesForChannel(channel as string)

  channelReceiverTypes.forEach(receiverType => {
    if (
      !(
        eventNotificationState.isEventCalendar &&
        receiverType === ReceiverType.ASSIGNED_USER
      )
    ) {
      notification.receiverType = receiverType as any
      notification.channel = channel
      const defaultNotification = getDefaultNotification(notification)
      if (defaultNotification) {
        defaultNotifications.push(defaultNotification as NotificationConfig)
      }
    }
  })
  return defaultNotifications
}

// WhatsApp-specific async version
export const getDefaultWhatsAppNotifications = async (
  notification: Partial<NotificationConfig>
) => {
  const defaultNotifications = [] as NotificationConfig[]
  const channel = notification.channel || NotificationChannel.WHATSAPP
  const channelReceiverTypes = getReceiverTypesForChannel(channel as string)

  for (const receiverType of channelReceiverTypes) {
    if (
      !(
        eventNotificationState.isEventCalendar &&
        receiverType === ReceiverType.ASSIGNED_USER
      )
    ) {
      notification.receiverType = receiverType as any
      notification.channel = channel
      const defaultNotification = await getDefaultWhatsAppNotification(notification)
      if (defaultNotification) {
        defaultNotifications.push(defaultNotification as NotificationConfig)
      }
    }
  }
  return defaultNotifications
}

export const getDefaultNotification = (
  notification: Partial<NotificationConfig>
): NotificationConfig | null => {
  const defaultTemplate = getDefaultEmailSubjectAndBody(
    notification,
    eventNotificationState.isCustomFormSelected
  )
  // Guest rows are only bootstrapped when the calendar collects guests —
  // mirrored for email and SMS (no guest default row when guests are disabled)
  if (
    (notification.channel === NotificationChannel.EMAIL ||
      notification.channel === NotificationChannel.SMS) &&
    notification.receiverType === ReceiverType.GUEST &&
    !AppState.editCalendarValidationData.enableGuests
  ) {
    return null
  }
  if (
    notification.channel === NotificationChannel.EMAIL &&
    eventNotificationState.isEventCalendar &&
    notification.receiverType === ReceiverType.ASSIGNED_USER
  ) {
    return null
  }
  // For non-WhatsApp channels, templateId remains empty
  let defaultTemplateId = ''

  const defaultNotification = {
    _id: notification._id || '',
    altType: AltType.CALENDAR,
    altId: notification.altId || '',
    receiverType: notification.receiverType,
    channel: notification.channel || NotificationChannel.EMAIL,
    notificationType: notification.notificationType,
    isActive: true,
    isDefault: true,
    templateId: defaultTemplateId,
    body: defaultTemplate.html || defaultTemplate.smsText || '',
    subject: defaultTemplate.subject,
    beforeTime: [],
    afterTime: [],
    selectedUsers: [],
    deleted: false,
  } as NotificationConfig

  // Add channel-specific fields
  const channelStr = notification.channel?.toString()
  if (channelStr === 'sms' || channelStr === 'whatsapp') {
    defaultNotification.additionalPhoneNumbers = []
  } else {
    defaultNotification.additionalEmailIds = []
  }

  return defaultNotification
}

/**
 * Resolve Settings defaults for Email/SMS internal receivers via backend locale packs.
 * Falls back to local English templates when the API fails or for out-of-scope receivers.
 * Caches the result so receiver-level Reset (incl. Contact/Guest) reuses the same pack copy.
 */
export const getLocalizedDefaultNotifications = async (
  notification: Partial<NotificationConfig>
): Promise<NotificationConfig[]> => {
  const channel = notification.channel || NotificationChannel.EMAIL
  const localDefaults = getDefaultNotifications({ ...notification, channel })

  if (
    channel !== NotificationChannel.EMAIL &&
    channel !== NotificationChannel.SMS
  ) {
    return localDefaults
  }

  if (!notification.altId || !notification.notificationType) {
    return localDefaults
  }

  const cacheKey = buildDefaultsCacheKey(
    notification.altId,
    channel.toString(),
    notification.notificationType
  )

  try {
    const { data } = await EventNotificationService.getNotificationDefaults({
      altId: notification.altId,
      altType: notification.altType || AltType.CALENDAR,
      channel: channel.toString(),
      notificationType: notification.notificationType,
    })
    const packDefaults = data?.defaults || []
    if (!packDefaults.length) {
      return localDefaults
    }

    const byReceiver = new Map(
      packDefaults.map(item => [item.receiverType, item])
    )

    const merged = localDefaults.map(local => {
      if (!supportsBackendI18nDefaults(local.channel, local.receiverType)) {
        return local
      }
      const pack = byReceiver.get(local.receiverType)
      if (!pack) {
        return local
      }
      return applyPackDefaultToLocal(local, pack)
    })
    localizedDefaultsCache.set(cacheKey, merged)
    return merged
  } catch (error) {
    console.warn(
      'Failed to resolve localized notification defaults; using local English fallback',
      error
    )
    return localDefaults
  }
}

export const getLocalizedDefaultNotification = async (
  notification: Partial<NotificationConfig>
): Promise<NotificationConfig | null> => {
  const channel = notification.channel || NotificationChannel.EMAIL
  if (
    !supportsBackendI18nDefaults(channel, notification.receiverType) ||
    !notification.altId ||
    !notification.notificationType ||
    !notification.receiverType
  ) {
    return getDefaultNotification(notification)
  }

  const local = getDefaultNotification(notification)
  if (!local) {
    return null
  }

  const cacheKey = buildDefaultsCacheKey(
    notification.altId,
    channel.toString(),
    notification.notificationType
  )
  const cached = localizedDefaultsCache
    .get(cacheKey)
    ?.find(item => item.receiverType === notification.receiverType)
  if (cached) {
    return applyPackDefaultToLocal(local, cached)
  }

  const fetchPackDefaults = async (includeReceiverType: boolean) => {
    const { data } = await EventNotificationService.getNotificationDefaults({
      altId: notification.altId!,
      altType: notification.altType || AltType.CALENDAR,
      channel: channel.toString(),
      notificationType: notification.notificationType!,
      ...(includeReceiverType
        ? { receiverType: notification.receiverType }
        : {}),
    })
    return data?.defaults || []
  }

  try {
    let packDefaults = await fetchPackDefaults(true)
    let pack = findPackDefaultForReceiver(
      packDefaults,
      notification.receiverType
    )

    // Contact/Guest (and older backends): retry unfiltered list used by modal load.
    if (!pack || pack.receiverType !== notification.receiverType) {
      packDefaults = await fetchPackDefaults(false)
      pack = findPackDefaultForReceiver(
        packDefaults,
        notification.receiverType
      )
      if (packDefaults.length) {
        // Warm cache from the same unfiltered response modal load uses.
        const byReceiver = new Map(
          packDefaults.map(item => [item.receiverType, item])
        )
        const localDefaults = getDefaultNotifications({
          ...notification,
          channel,
        })
        localizedDefaultsCache.set(
          cacheKey,
          localDefaults.map(row => {
            if (!supportsBackendI18nDefaults(row.channel, row.receiverType)) {
              return row
            }
            const rowPack = byReceiver.get(row.receiverType)
            return rowPack ? applyPackDefaultToLocal(row, rowPack) : row
          })
        )
      }
    }

    if (!pack || pack.receiverType !== notification.receiverType) {
      return local
    }
    return applyPackDefaultToLocal(local, pack)
  } catch (error) {
    console.warn(
      'Failed to resolve localized notification default; using local English fallback',
      error
    )
    try {
      const packDefaults = await fetchPackDefaults(false)
      const pack = findPackDefaultForReceiver(
        packDefaults,
        notification.receiverType
      )
      if (pack && pack.receiverType === notification.receiverType) {
        return applyPackDefaultToLocal(local, pack)
      }
    } catch (fallbackError) {
      console.warn(
        'Failed unfiltered defaults fallback after filtered request error',
        fallbackError
      )
    }
    return local
  }
}

/** Mark configurator state customized after subject/body/templateId edits. */
export const markNotificationContentCustomized = (
  state: Partial<NotificationConfig>
) => {
  if (state.isDefault !== false) {
    state.isDefault = false
  }
}

export type NotificationContentPatch = {
  subject?: string
  body?: string
  templateId?: string
}

/**
 * Apply subject/body/templateId edits onto a notification row.
 * Flips `isDefault` to false only when those content fields actually change
 * (GHLPRD-20514: save/hydrate without real edits must not mark customized).
 */
export const syncEditedNotificationContent = (
  target: Partial<NotificationConfig>,
  patch: NotificationContentPatch
): boolean => {
  let contentChanged = false
  ;(['subject', 'body', 'templateId'] as const).forEach(key => {
    if (patch[key] === undefined) {
      return
    }
    if (target[key] !== patch[key]) {
      target[key] = patch[key]
      contentChanged = true
    }
  })
  if (contentChanged) {
    markNotificationContentCustomized(target)
  }
  return contentChanged
}

/**
 * Merge list API rows with pack defaults for Settings UI display.
 * - Missing receiver → append pack default (inactive)
 * - List row with `isDefault: true` → overlay subject/body from pack defaults API
 *   (keeps _id, isActive, schedules); clears templateId
 * - List row with `isDefault` false/missing → keep customized stored content
 */
export const mergeListNotificationsWithPackDefaults = (
  listNotifications: NotificationConfig[],
  packDefaults: NotificationConfig[]
): NotificationConfig[] => {
  const merged = [...listNotifications]

  packDefaults.forEach(packDefault => {
    const index = merged.findIndex(
      row => row.receiverType === packDefault.receiverType
    )
    if (index === -1) {
      merged.push(Object.assign({}, packDefault, { isActive: false }))
      return
    }

    const row = merged[index]
    if (row.isDefault === true) {
      merged[index] = {
        ...row,
        subject: packDefault.subject ?? '',
        body: packDefault.body || '',
        templateId: '',
        isDefault: true,
      }
    }
  })

  return merged
}

// WhatsApp-specific async version that resolves template IDs
export const getDefaultWhatsAppNotification = async (
  notification: Partial<NotificationConfig>
): Promise<NotificationConfig | null> => {
  const defaultTemplate = getDefaultEmailSubjectAndBody(
    notification,
    eventNotificationState.isCustomFormSelected
  )
  if (
    notification.channel === NotificationChannel.EMAIL &&
    ((notification.receiverType === ReceiverType.GUEST &&
      !AppState.editCalendarValidationData.enableGuests) ||
      (eventNotificationState.isEventCalendar &&
        notification.receiverType === ReceiverType.ASSIGNED_USER))
  ) {
    return null
  }

  // Get default template ID for WhatsApp notifications
  let defaultTemplateId = ''
  if (notification.channel?.toString() === 'whatsapp') {
    const defaultTemplateLabel = getDefaultWhatsAppTemplateLabel(
      notification.notificationType || '',
      notification.receiverType || ''
    )

    if (defaultTemplateLabel) {
      try {
        // Fetch WhatsApp templates to get the actual template ID
        const whatsappTemplates = await getWhatsAppTemplates()
        const defaultTemplate = whatsappTemplates.find(template =>
            template.label &&
            template.label.toLowerCase() === defaultTemplateLabel.toLowerCase()
        )

        if (defaultTemplate) {
          defaultTemplateId = defaultTemplate.value as string
        }
      } catch (error) {
        console.warn('Error fetching WhatsApp templates for default notification:', error)
      }
    }
  }

  const defaultNotification = {
    _id: notification._id || '',
    altType: AltType.CALENDAR,
    altId: notification.altId || '',
    receiverType: notification.receiverType,
    channel: notification.channel || NotificationChannel.EMAIL,
    notificationType: notification.notificationType,
    isActive: true,
    isDefault: true,
    templateId: defaultTemplateId,
    body: defaultTemplate.html || defaultTemplate.smsText || '',
    subject: defaultTemplate.subject,
    beforeTime: [],
    afterTime: [],
    selectedUsers: [],
    deleted: false,
  } as NotificationConfig

  // Add channel-specific fields
  const channelStr = notification.channel?.toString()
  if (channelStr === 'sms' || channelStr === 'whatsapp') {
    defaultNotification.additionalPhoneNumbers = []
  } else {
    defaultNotification.additionalEmailIds = []
  }

  return defaultNotification
}

export function normalizeNotificationApiResponse(
  data: NotificationConfig | NotificationConfig[] | undefined | null
): NotificationConfig[] {
  if (!data) {
    return []
  }
  return Array.isArray(data) ? data : [data]
}

type ReceiverConfiguratorBucket = {
  state: NotificationConfig
}

function getReceiverConfiguratorBucket(
  receiverType: string
): ReceiverConfiguratorBucket | undefined {
  const bucket = eventNotificationState[receiverType as keyof typeof eventNotificationState]
  if (bucket && typeof bucket === 'object' && 'state' in bucket) {
    return bucket as ReceiverConfiguratorBucket
  }
  return undefined
}

function normalizeNotificationChannel(
  channel?: string | NotificationChannel
): string | undefined {
  return channel?.toString().toLowerCase()
}

function inferNotificationChannel(receiverType: string): string | undefined {
  if (
    eventNotificationState.smsNotifications.some(
      notification => notification.receiverType === receiverType
    )
  ) {
    return NotificationChannel.SMS
  }
  if (
    eventNotificationState.whatsappNotifications.some(
      notification => notification.receiverType === receiverType
    )
  ) {
    return NotificationChannel.WHATSAPP
  }
  if (
    eventNotificationState.eventNotifications.some(
      notification => notification.receiverType === receiverType
    )
  ) {
    return NotificationChannel.EMAIL
  }
  return undefined
}

/** Resolve persisted _id by receiverType — never by response or UI array index. */
export function resolvePersistedNotificationId(
  receiverType: string,
  configRows: NotificationConfig[],
  receiverState?: NotificationConfig
): string | undefined {
  const configId = configRows.find(row => row.receiverType === receiverType)?._id
  const stateId = receiverState?._id

  if (!isVirtualNotification(configId)) {
    return configId
  }
  if (!isVirtualNotification(stateId)) {
    return stateId
  }
  return configId || stateId || undefined
}

/** Merge editor state with config-array rows; config array is source of truth for persisted _id. */
export function buildSavePayloadFromNotifications(
  notifications: NotificationConfig[]
): NotificationConfig[] {
  return notifications.map(notification => {
    const receiverState = getReceiverConfiguratorBucket(notification.receiverType)?.state
    const persistedId = resolvePersistedNotificationId(
      notification.receiverType,
      notifications,
      receiverState
    )
    if (!receiverState) {
      return persistedId ? { ...notification, _id: persistedId } : notification
    }
    return {
      ...receiverState,
      _id: persistedId ?? '',
      receiverType: notification.receiverType,
      channel: notification.channel ?? receiverState.channel,
    }
  })
}

function syncReceiverConfiguratorState(
  notification: NotificationConfig,
  overrides?: Partial<NotificationConfig>
) {
  const receiverBucket = getReceiverConfiguratorBucket(notification.receiverType)
  if (!receiverBucket) {
    return
  }
  const existingState = receiverBucket.state
  const preservedBeforeTime =
    notification.beforeTime?.length === 0 && existingState.beforeTime?.length > 0
      ? existingState.beforeTime
      : notification.beforeTime
  const preservedAfterTime =
    notification.afterTime?.length === 0 && existingState.afterTime?.length > 0
      ? existingState.afterTime
      : notification.afterTime

  Object.assign(receiverBucket.state, notification, overrides, {
    _id: notification._id,
    receiverType: notification.receiverType,
    beforeTime: preservedBeforeTime,
    afterTime: preservedAfterTime,
  })
}

/**
 * Build create payload from a Settings virtual/default row.
 * Always persist `isDefault: true` on first create so locale packs stay live
 * (TipTap/hydrate can falsely flip the in-memory flag before enable/save).
 */
export function stripNotificationIdForCreate<T extends { _id?: string; isDefault?: boolean }>(
  notification: T
): Omit<T, '_id'> {
  const { _id: _ignored, ...rest } = notification
  return { ...rest, isDefault: true }
}

export const isVirtualNotification = (id: string | undefined | null): boolean => {
  return !id
}

export const isInAppNotificationChannel = (channel?: string | NotificationChannel): boolean => {
  return channel?.toString() === NotificationChannel.IN_APP
}

export function applyInAppCreateResult(
  data: NotificationConfig | NotificationConfig[] | undefined | null,
  deleted?: boolean
) {
  const created = normalizeNotificationApiResponse(data)[0]
  if (!created?._id) {
    return
  }
  eventNotificationState.inAppNotification._id = created._id
  if (deleted !== undefined) {
    eventNotificationState.inAppNotification.deleted = deleted
  }
}

function upsertChannelNotificationRow(
  targetConfig: NotificationConfig[],
  notification: NotificationConfig
) {
  const index = targetConfig.findIndex(n => n.receiverType === notification.receiverType)
  if (index !== -1) {
    targetConfig[index] = {
      ...targetConfig[index],
      ...notification,
      _id: notification._id ?? targetConfig[index]._id,
      receiverType: notification.receiverType,
    }
  } else {
    targetConfig.push(notification)
  }
}

export function updateNotificationState(
  data: NotificationConfig | NotificationConfig[],
  deleted?: boolean
) {
  normalizeNotificationApiResponse(data).forEach(originalNotification => {
    const notification = {
      ...originalNotification,
      deleted: deleted !== undefined ? deleted : originalNotification.deleted,
    }

    notification.body = formatHTMLForTipTap(notification.body)

    if (!notification.channel) {
      const inferredChannel = inferNotificationChannel(notification.receiverType)
      if (inferredChannel) {
        notification.channel = inferredChannel
      }
    }

    if (isInAppNotificationChannel(notification.channel)) {
      return
    }

    const channel = normalizeNotificationChannel(notification.channel)

    if (channel === NotificationChannel.SMS) {
      upsertChannelNotificationRow(eventNotificationState.smsNotifications, notification)
    } else if (channel === NotificationChannel.WHATSAPP) {
      upsertChannelNotificationRow(
        eventNotificationState.whatsappNotifications,
        notification
      )
    } else {
      upsertChannelNotificationRow(eventNotificationState.eventNotifications, notification)
    }

    syncReceiverConfiguratorState(notification)
  })

  // Sort all notification arrays to maintain consistent ordering after updates
  eventNotificationState.eventNotifications = sortNotificationsByReceiverType(
    eventNotificationState.eventNotifications,
    NotificationChannel.EMAIL
  )
  eventNotificationState.smsNotifications = sortNotificationsByReceiverType(
    eventNotificationState.smsNotifications,
    NotificationChannel.SMS
  )
  eventNotificationState.whatsappNotifications = sortNotificationsByReceiverType(
      eventNotificationState.whatsappNotifications,
      'whatsapp'
    )
}

export function getEventNotificationState(tab: string) {
  // check if all the email notification are enabled
  // all are disabled
  let isNotificationsEnabled = true
  if (tab === 'inApp') {
    const isAvailableInDb = !!eventNotificationState.inAppNotification._id
    if (!isAvailableInDb) {
      return !!isAvailableInDb
    }
    return !eventNotificationState.inAppNotification.deleted
  }
  if (tab === 'email') {
    const isAvailableInDb = eventNotificationState.eventNotifications.find(
      notification => notification._id
    )
    if (!isAvailableInDb) {
      return !!isAvailableInDb
    }
    if (
      eventNotificationState.eventNotifications.every(
        notification => notification.deleted
      )
    ) {
      isNotificationsEnabled = false
    }
    if (
      eventNotificationState.inAppNotification.notificationType === 'booked'
    ) {
      const isStoredData = eventNotificationState.eventNotifications.find(
        notification => notification._id
      )
      if (!isStoredData) {
        return !eventNotificationState.eventNotifications
      }
    }
  }
  if (tab === 'sms') {
    const isAvailableInDb = eventNotificationState.smsNotifications.find(
      notification => notification._id
    )
    if (!isAvailableInDb) {
      return !!isAvailableInDb
    }
    if (
      eventNotificationState.smsNotifications.every(
        notification => notification.deleted
      )
    ) {
      isNotificationsEnabled = false
    }
    if (
      eventNotificationState.inAppNotification.notificationType === 'booked'
    ) {
      const isStoredData = eventNotificationState.smsNotifications.find(
        notification => notification._id
      )
      if (!isStoredData) {
        return !eventNotificationState.smsNotifications
      }
    }
  }
  if (tab === 'whatsapp') {
    const isAvailableInDb = eventNotificationState.whatsappNotifications.find(
      notification => notification._id
    )
    if (!isAvailableInDb) {
      return !!isAvailableInDb
    }
    if (
      eventNotificationState.whatsappNotifications.every(
        notification => notification.deleted
      )
    ) {
      isNotificationsEnabled = false
    }
    if (
      eventNotificationState.inAppNotification.notificationType === 'booked'
    ) {
      const isStoredData = eventNotificationState.whatsappNotifications.find(
        notification => notification._id
      )
      if (!isStoredData) {
        return !eventNotificationState.whatsappNotifications
      }
    }
  }
  return isNotificationsEnabled
}
